import React, { useState, useCallback, useEffect } from "react";
import SectionTitle from "./SectionTitle";
import { IMAGES } from "../../data/site";

const officeImages = [
  { id: 1, src: IMAGES.office1, alt: "Galleria Dental – reception area" },
  { id: 2, src: IMAGES.office2, alt: "Galleria Dental – waiting lounge" },
  { id: 3, src: IMAGES.office3, alt: "Galleria Dental – treatment room" },
  { id: 4, src: IMAGES.office4, alt: "Galleria Dental – consultation suite" },
  { id: 5, src: IMAGES.office5, alt: "Galleria Dental – front desk" },
  { id: 6, src: IMAGES.office6, alt: "Galleria Dental – hallway" },
  { id: 7, src: IMAGES.office7, alt: "Galleria Dental – sterilisation area" },
  { id: 8, src: IMAGES.office8, alt: "Galleria Dental – patient chair" },
  { id: 9, src: IMAGES.office9, alt: "Galleria Dental – panoramic view" },
  { id: 10, src: IMAGES.office10, alt: "Galleria Dental – exterior entrance" },
];

/**
 * Exact 4-col × 4-row = 16-cell gap-free layout
 *
 * Row 1-2: [img0 2×2 hero]  [img1 1×1]  [img2 1×1]
 * Row 2:   [img0 cont]       [img3 1×1]  [img4 1×1]
 * Row 3:   [img5 1×1]  [img6 2×1 wide]  [img7 1×1]
 * Row 4:   [img8 2×1 wide]  [img9 2×1 wide]
 */
const layout = [
  { col: "col-span-1 md:col-span-2", row: "row-span-1 md:row-span-2" },
  { col: "col-span-1", row: "row-span-1" },
  { col: "col-span-1", row: "row-span-1" },
  { col: "col-span-1", row: "row-span-1" },
  { col: "col-span-1", row: "row-span-1" },
  { col: "col-span-1", row: "row-span-1" },
  { col: "col-span-1 md:col-span-2", row: "row-span-1" },
  { col: "col-span-1", row: "row-span-1" },
  { col: "col-span-1 md:col-span-2", row: "row-span-1" },
  { col: "col-span-1 md:col-span-2", row: "row-span-1" },
];

const OfficeGallery = () => {
  const [lightbox, setLightbox] = useState(null);

  const open = useCallback((idx) => setLightbox(idx), []);
  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(
    () =>
      setLightbox((i) => (i - 1 + officeImages.length) % officeImages.length),
    [],
  );
  const next = useCallback(
    () => setLightbox((i) => (i + 1) % officeImages.length),
    [],
  );

  useEffect(() => {
    if (lightbox === null) return;
    const handler = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, close, prev, next]);

  useEffect(() => {
    document.body.style.overflow = lightbox !== null ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <section className="bg-[#ffffff] py-20 md:py-24 border-t border-[#B79A68]/15">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionTitle
          script=""
          title="Our Office"
          subtitle="A look inside Galleria Dental"
        />

        {/* ── Combined 4-col × 4-row gallery grid ── */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 grid-flow-dense auto-rows-[160px] md:auto-rows-[200px] gap-3">
          {officeImages.map((img, idx) => (
            <div
              key={img.id}
              onClick={() => open(idx)}
              className={`group relative overflow-hidden cursor-pointer border border-[#B79A68]/25 bg-[#FBF8F1] ${layout[idx].col} ${layout[idx].row}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.07]"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-[#1a1209]/0 group-hover:bg-[#1a1209]/40 transition-colors duration-300 flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-9 h-9 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15zM10.5 7.5v6m-3-3h6"
                  />
                </svg>
              </div>
            </div>
          ))}
        </div>

        {/* ── Lightbox ── */}
        {lightbox !== null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={close}
          >
            {/* Prev */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prev();
              }}
              className="absolute left-4 md:left-8 z-10 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors focus:outline-none"
              aria-label="Previous image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            {/* Image */}
            <div
              className="relative max-w-5xl w-full mx-20 flex flex-col items-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={officeImages[lightbox].src}
                alt={officeImages[lightbox].alt}
                className="max-h-[80vh] max-w-full object-contain shadow-2xl"
              />
              <div className="mt-4 flex items-center gap-3">
                <span className="h-px w-8 bg-[#B79A68]/50" />
                <p className="text-white/55 text-sm tracking-widest uppercase">
                  {lightbox + 1}&nbsp;/&nbsp;{officeImages.length}
                </p>
                <span className="h-px w-8 bg-[#B79A68]/50" />
              </div>
              <p className="mt-1 text-white/35 text-xs tracking-wide">
                {officeImages[lightbox].alt}
              </p>
            </div>

            {/* Next */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                next();
              }}
              className="absolute right-4 md:right-8 z-10 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors focus:outline-none"
              aria-label="Next image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>

            {/* Close */}
            <button
              onClick={close}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors focus:outline-none"
              aria-label="Close lightbox"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Thumbnail strip */}
            <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 px-4 overflow-x-auto">
              {officeImages.map((img, idx) => (
                <button
                  key={img.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    open(idx);
                  }}
                  className={`flex-shrink-0 w-12 h-8 overflow-hidden transition-all duration-200 ${
                    lightbox === idx
                      ? "ring-2 ring-[#B79A68] opacity-100 scale-110"
                      : "opacity-40 hover:opacity-75"
                  }`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img
                    src={img.src}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default OfficeGallery;
